
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { withRouter } from 'react-router-dom';
import Navbar from 'react-bootstrap/Navbar';
import {
    FaUser,
    FaSignOutAlt,
    FaTachometerAlt,
    FaTasks,
    FaMapMarkedAlt,
    FaEuroSign,
    FaFileAlt,
    FaChartPie,
    FaUsers,
    FaBars,
    FaTimes,
    FaAngleDoubleLeft,
    FaAngleDoubleRight,
} from 'react-icons/fa';
import './Menu.css';
import { getTheme } from '../../theme/palette';
import { headerLogo, brandLogo, crestLogo } from '../../theme/brandAssets';
import DisplayModeSwitch from '../../theme/DisplayModeSwitch';
import { useI18n } from '../../i18n';
import LanguageSwitch from '../../i18n/LanguageSwitch';

const theme = getTheme();
const navTheme = theme.navTheme;
const navMark = theme.navMark || 'contain';
const header_logo = headerLogo;
const brand_logo = brandLogo;
const compact_logo = crestLogo || headerLogo;

const NAV_COMPACT_KEY = 'ipsum.nav.compact';
const SIDEBAR_EXPANDED = '220px';
const SIDEBAR_COMPACT = '68px';
const TABLET_MQ = '(max-width: 1199px)';

const getInitials = (fullname) => {
    if (!fullname) return '';
    return fullname
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(word => word.charAt(0).toUpperCase())
        .join('');
};

const readCompactPref = () => {
    try {
        const saved = window.localStorage.getItem(NAV_COMPACT_KEY);
        if (saved === '1') return true;
        if (saved === '0') return false;
    } catch (err) { /* keep default */ }
    return window.matchMedia(TABLET_MQ).matches;
};

function SidebarLink({ href, active, onClick, tip, children }) {
    return (
        <a
            href={href}
            className={`sidebar-link${active ? ' is-active' : ''}`}
            aria-current={active ? 'page' : undefined}
            aria-label={tip}
            data-tip={tip}
            onClick={onClick}
        >
            {children}
        </a>
    );
}

function SidebarGroup({ id, label, icon: Icon, active, open, compact, onToggle, children }) {
    const btnRef = useRef(null);
    const [flyoutPos, setFlyoutPos] = useState(null);

    useEffect(() => {
        if (!compact || !open || !btnRef.current) {
            setFlyoutPos(null);
            return undefined;
        }
        const place = () => {
            const rect = btnRef.current.getBoundingClientRect();
            setFlyoutPos({ top: rect.top, left: rect.right + 8 });
        };
        place();
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);
        return () => {
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
        };
    }, [compact, open]);

    const flyout = compact && open && flyoutPos
        ? createPortal(
            <div
                className="sidebar-flyout"
                style={{ top: flyoutPos.top, left: flyoutPos.left }}
                role="menu"
            >
                <div className="sidebar-flyout-title">{label}</div>
                {children}
            </div>,
            document.body
        )
        : null;

    return (
        <div className={`sidebar-group${active ? ' is-current' : ''}${open ? ' is-open' : ''}`}>
            <button
                ref={btnRef}
                type="button"
                className="sidebar-group-toggle"
                aria-expanded={open}
                aria-label={label}
                data-tip={label}
                onClick={() => onToggle(id)}
            >
                {Icon ? <Icon className="sidebar-icon" aria-hidden="true" /> : null}
                <span>{label}</span>
            </button>
            {!compact && open ? <div className="sidebar-sub">{children}</div> : null}
            {flyout}
        </div>
    );
}

function Menu(props) {
    const { t } = useI18n();
    const productName = t('common.productName');
    const [openGroup, setOpenGroup] = useState('');
    const [mobileOpen, setMobileOpen] = useState(false);
    const [compactPref, setCompactPref] = useState(readCompactPref);
    const compact = compactPref;

    const handleLogout = async () => {
        setMobileOpen(false);
        props.onLogout();
        props.history.push('/login');
    };

    const navigate = (event, path) => {
        if (event) event.preventDefault();
        setMobileOpen(false);
        if (compact) setOpenGroup('');
        if (props.location && props.location.pathname === path) return;
        props.history.push(path);
    };

    const activeKey = props.location ? props.location.pathname : '/';

    const tasksActive =
        activeKey === '/tasks' ||
        activeKey === '/tasks_comp' ||
        activeKey === '/task_board' ||
        (activeKey.startsWith('/task_') && activeKey !== '/tasks_costs');
    const assetsActive =
        activeKey === '/assets' ||
        activeKey === '/asset_locator' ||
        activeKey.startsWith('/asset_');
    const costsActive =
        activeKey === '/cost_add' ||
        activeKey === '/tasks_costs' ||
        activeKey === '/inventory';
    const reportsActive = activeKey === '/monthly_report';
    const usersActive =
        activeKey === '/users' ||
        activeKey === '/signup' ||
        activeKey.startsWith('/user_det');
    const dashboardActive = activeKey === '/';
    const kpisActive = activeKey === '/kpis';

    useEffect(() => {
        const app = document.querySelector('.App');
        if (!app) return undefined;
        app.classList.toggle('is-nav-compact', compact);
        app.style.setProperty('--app-sidebar-width', compact ? SIDEBAR_COMPACT : SIDEBAR_EXPANDED);
        return () => {
            app.classList.remove('is-nav-compact');
            app.style.setProperty('--app-sidebar-width', SIDEBAR_EXPANDED);
        };
    }, [compact]);

    useEffect(() => {
        if (compact) {
            setOpenGroup('');
            setMobileOpen(false);
            return;
        }
        if (tasksActive) setOpenGroup('tasks');
        else if (assetsActive) setOpenGroup('assets');
        else if (costsActive) setOpenGroup('costs');
        else if (reportsActive) setOpenGroup('reports');
        else if (usersActive) setOpenGroup('users');
        else setOpenGroup('');
        setMobileOpen(false);
    }, [activeKey, compact, tasksActive, assetsActive, costsActive, reportsActive, usersActive]);

    useEffect(() => {
        if (!mobileOpen && !(compact && openGroup)) return undefined;
        const onKey = (event) => {
            if (event.key !== 'Escape') return;
            setMobileOpen(false);
            if (compact) setOpenGroup('');
        };
        const onPointer = (event) => {
            if (!compact || !openGroup) return;
            if (event.target.closest('.app-sidebar') || event.target.closest('.sidebar-flyout')) return;
            setOpenGroup('');
        };
        window.addEventListener('keydown', onKey);
        document.addEventListener('mousedown', onPointer);
        return () => {
            window.removeEventListener('keydown', onKey);
            document.removeEventListener('mousedown', onPointer);
        };
    }, [mobileOpen, compact, openGroup]);

    const toggleGroup = (id) => {
        setOpenGroup((current) => (current === id ? '' : id));
    };

    const toggleCompact = () => {
        setCompactPref((current) => {
            const next = !current;
            try { window.localStorage.setItem(NAV_COMPACT_KEY, next ? '1' : '0'); } catch (err) { /* ignore */ }
            setOpenGroup('');
            return next;
        });
    };

    if (!props.isAuthenticated) {
        return (
            <div className={`Menu is-topbar menu-env-${process.env.REACT_APP_GLOB_APP_ENV || 'default'}`}>
                <Navbar variant={navTheme === 'light' ? 'light' : 'dark'} className="p-0 pl-4">
                    <Navbar.Brand>
                        <img
                            src={brand_logo}
                            className="brand_logo d-inline-block align-top"
                            alt={productName}
                        />
                    </Navbar.Brand>
                    <Navbar.Text className="p-0">
                        <span className="nav-product">{productName}</span>
                    </Navbar.Text>
                </Navbar>
            </div>
        );
    }

    return (
        <div
            className={`Menu is-sidebar menu-env-${process.env.REACT_APP_GLOB_APP_ENV || 'default'}${mobileOpen ? ' is-open' : ''}${compact ? ' is-compact' : ''}`}
            data-nav-mark={navMark}
        >
            <div className="sidebar-mobilebar">
                <button
                    type="button"
                    className="sidebar-menu-btn"
                    aria-label={mobileOpen ? t('nav.closeMenu') : t('nav.openMenu')}
                    aria-expanded={mobileOpen}
                    onClick={() => setMobileOpen((open) => !open)}
                >
                    {mobileOpen ? <FaTimes /> : <FaBars />}
                </button>
                <a className="title" href="/" onClick={(e) => navigate(e, '/')}>
                    <img
                        src={header_logo}
                        className="header_logo"
                        alt={productName}
                    />
                </a>
            </div>
            {mobileOpen ? (
                <button
                    type="button"
                    className="sidebar-backdrop"
                    aria-label={t('nav.closeMenu')}
                    onClick={() => setMobileOpen(false)}
                />
            ) : null}
            <aside className="app-sidebar">
                <div className="sidebar-brand">
                    <a className="title" href="/" onClick={(e) => navigate(e, '/')}>
                        <span className="sidebar-mark">
                            <img
                                src={header_logo}
                                className="header_logo is-full"
                                alt={productName}
                            />
                            <img
                                src={compact_logo}
                                className="header_logo is-mark"
                                alt=""
                            />
                        </span>
                    </a>
                    <button
                        type="button"
                        className="sidebar-compact-btn"
                        aria-label={compact ? t('nav.expandMenu') : t('nav.collapseMenu')}
                        title={compact ? t('nav.expandMenu') : t('nav.collapseMenu')}
                        onClick={toggleCompact}
                    >
                        {compact ? <FaAngleDoubleRight /> : <FaAngleDoubleLeft />}
                    </button>
                </div>
                <nav className="sidebar-nav" aria-label={t('nav.main')}>
                    <SidebarLink href="/" active={dashboardActive} tip={t('nav.dashboard')} onClick={(e) => navigate(e, '/')}>
                        <FaTachometerAlt className="sidebar-icon" aria-hidden="true" />
                        <span>{t('nav.dashboard')}</span>
                    </SidebarLink>
                    <SidebarGroup
                        id="tasks"
                        label={t('nav.tasks')}
                        icon={FaTasks}
                        active={tasksActive}
                        open={openGroup === 'tasks'}
                        compact={compact}
                        onToggle={toggleGroup}
                    >
                        <SidebarLink href="/task_add" active={activeKey === '/task_add'} tip={t('nav.addTask')} onClick={(e) => navigate(e, '/task_add')}>{t('nav.addTask')}</SidebarLink>
                        <SidebarLink href="/tasks" active={activeKey === '/tasks'} tip={t('nav.actives')} onClick={(e) => navigate(e, '/tasks')}>{t('nav.actives')}</SidebarLink>
                        <SidebarLink href="/tasks_comp" active={activeKey === '/tasks_comp'} tip={t('nav.completed')} onClick={(e) => navigate(e, '/tasks_comp')}>{t('nav.completed')}</SidebarLink>
                        <SidebarLink href="/task_board" active={activeKey === '/task_board'} tip={t('nav.taskBoard')} onClick={(e) => navigate(e, '/task_board')}>{t('nav.taskBoard')}</SidebarLink>
                    </SidebarGroup>
                    <SidebarGroup
                        id="assets"
                        label={t('nav.assets')}
                        icon={FaMapMarkedAlt}
                        active={assetsActive}
                        open={openGroup === 'assets'}
                        compact={compact}
                        onToggle={toggleGroup}
                    >
                        <SidebarLink href="/assets" active={activeKey === '/assets'} tip={t('nav.assets')} onClick={(e) => navigate(e, '/assets')}>{t('nav.assets')}</SidebarLink>
                        <SidebarLink href="/asset_locator" active={activeKey === '/asset_locator'} tip={t('nav.assetLocator')} onClick={(e) => navigate(e, '/asset_locator')}>{t('nav.assetLocator')}</SidebarLink>
                    </SidebarGroup>
                    <SidebarGroup
                        id="costs"
                        label={t('nav.costs')}
                        icon={FaEuroSign}
                        active={costsActive}
                        open={openGroup === 'costs'}
                        compact={compact}
                        onToggle={toggleGroup}
                    >
                        <SidebarLink href="/cost_add" active={activeKey === '/cost_add'} tip={t('nav.preliminaries')} onClick={(e) => navigate(e, '/cost_add')}>{t('nav.preliminaries')}</SidebarLink>
                        <SidebarLink href="/tasks_costs" active={activeKey === '/tasks_costs'} tip={t('nav.costManager')} onClick={(e) => navigate(e, '/tasks_costs')}>{t('nav.costManager')}</SidebarLink>
                        <SidebarLink href="/inventory" active={activeKey === '/inventory'} tip={t('nav.inventory')} onClick={(e) => navigate(e, '/inventory')}>{t('nav.inventory')}</SidebarLink>
                    </SidebarGroup>
                    <SidebarGroup
                        id="reports"
                        label={t('nav.reports')}
                        icon={FaFileAlt}
                        active={reportsActive}
                        open={openGroup === 'reports'}
                        compact={compact}
                        onToggle={toggleGroup}
                    >
                        <SidebarLink href="/monthly_report" active={activeKey === '/monthly_report'} tip={t('nav.monthlyReport')} onClick={(e) => navigate(e, '/monthly_report')}>{t('nav.monthlyReport')}</SidebarLink>
                    </SidebarGroup>
                    <SidebarLink href="/kpis" active={kpisActive} tip={t('nav.kpis')} onClick={(e) => navigate(e, '/kpis')}>
                        <FaChartPie className="sidebar-icon" aria-hidden="true" />
                        <span>{t('nav.kpis')}</span>
                    </SidebarLink>
                    {props.user && props.user.administrator === 'true' ? (
                        <SidebarGroup
                            id="users"
                            label={t('nav.users')}
                            icon={FaUsers}
                            active={usersActive}
                            open={openGroup === 'users'}
                            compact={compact}
                            onToggle={toggleGroup}
                        >
                            <SidebarLink href="/signup" active={activeKey === '/signup'} tip={t('nav.addUser')} onClick={(e) => navigate(e, '/signup')}>{t('nav.addUser')}</SidebarLink>
                            <SidebarLink href="/users" active={activeKey === '/users'} tip={t('nav.users')} onClick={(e) => navigate(e, '/users')}>{t('nav.users')}</SidebarLink>
                        </SidebarGroup>
                    ) : null}
                </nav>
                <div className="sidebar-user">
                    <DisplayModeSwitch />
                    {props.userFullname ? (
                        <div className="user-chip" data-tip={props.userFullname} title={props.userFullname}>
                            <span className="user-avatar">
                                {getInitials(props.userFullname) || <FaUser className="user-icon" />}
                            </span>
                            <span className="user-meta">
                                <span className="user-name">{props.userFullname}</span>
                            </span>
                        </div>
                    ) : null}
                    <LanguageSwitch variant="sidebar" />
                    <button
                        type="button"
                        className="logout-btn"
                        onClick={handleLogout}
                        data-tip={t('nav.logout')}
                        aria-label={t('nav.logout')}
                    >
                        <FaSignOutAlt className="logout-icon" />
                        <span>{t('nav.logout')}</span>
                    </button>
                </div>
            </aside>
        </div>
    );
}

export default withRouter(Menu);
